import {
  Box,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import type { ChangeEvent, Dispatch, SetStateAction } from "react"
import Select from "@/components/Common/AppSelect"
import type { ProductResponceDTO } from "../../../client"
import ContragentSelect from "../Contragents/ContragentSelect"
import ContragentsMultiSelect from "../Contragents/ContragentsMultiSelect"
import ExtraDataFields, {
  type ExtraDataItem,
  jsonToExtraData,
} from "./ExtraDataFields"
import { PRODUCT_UNIT_OPTIONS } from "./unitOptions"

export type ProductFormState = {
  sku: string
  name: string
  manufacturer: string
  manufacturer_contragent_id: string
  manufacturer_contragent_name: string
  supplier_ids: string[]
  EAN: string
  unit: string
  default_min_qty: number | undefined
  unit_weight_kg: string
  catalog_price_percent: number | undefined
  description: string
  extra_data_items: ExtraDataItem[]
  is_active: boolean
  requires_batch_tracking: boolean
  requires_expiration_date: boolean
}

type ProductFormErrors = {
  sku?: string
  name?: string
  unit?: string
  manufacturer?: string
  unit_weight_kg?: string
}

type Props = {
  form: ProductFormState
  setForm: Dispatch<SetStateAction<ProductFormState>>
  errors?: ProductFormErrors
  manufacturerRequired?: boolean
  showActiveToggle?: boolean
}

export function createEmptyProductFormState(): ProductFormState {
  return {
    sku: "",
    name: "",
    manufacturer: "",
    manufacturer_contragent_id: "",
    manufacturer_contragent_name: "",
    supplier_ids: [],
    EAN: "",
    unit: "",
    default_min_qty: undefined,
    unit_weight_kg: "",
    catalog_price_percent: undefined,
    description: "",
    extra_data_items: [],
    is_active: true,
    requires_batch_tracking: false,
    requires_expiration_date: false,
  }
}

export function productResponseToFormState(
  product: ProductResponceDTO,
): ProductFormState {
  return {
    sku: product.sku ?? "",
    name: product.name ?? "",
    manufacturer: product.manufacturer ?? "",
    manufacturer_contragent_id: product.manufacturer_contragent_id ?? "",
    manufacturer_contragent_name:
      product.manufacturer_contragent?.full_name ?? product.manufacturer ?? "",
    supplier_ids: (product.suppliers ?? []).map((item) => item.id),
    EAN: product.EAN ?? "",
    unit: product.unit ?? "",
    default_min_qty: product.default_min_qty,
    unit_weight_kg:
      (product as ProductResponceDTO & { unit_weight_kg?: string | null })
        .unit_weight_kg ?? "",
    catalog_price_percent: product.catalog_price_percent,
    description: product.description ?? "",
    extra_data_items: jsonToExtraData(product.extra_data),
    is_active: !!product.is_active,
    requires_batch_tracking: !!product.requires_batch_tracking,
    requires_expiration_date: !!product.requires_expiration_date,
  }
}

export default function ProductFormFields({
  form,
  setForm,
  errors,
  manufacturerRequired = false,
  showActiveToggle = false,
}: Props) {
  const panelBg = useColorModeValue("white", "whiteAlpha.50")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.600", "gray.400")
  const labelColor = useColorModeValue("gray.600", "gray.300")
  const defaultMinQtyInput =
    form.default_min_qty == null ? "" : String(form.default_min_qty)
  const unitWeightInput = form.unit_weight_kg
  const catalogPricePercentInput =
    form.catalog_price_percent == null ? "" : String(form.catalog_price_percent)

  return (
    <>
      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="md"
        bg={panelBg}
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Dane podstawowe
            </Text>
            <Text fontSize="sm" color={mutedText} mt={1}>
              Najważniejsze informacje widoczne w dokumentach, wyszukiwarce i na
              etykietach.
            </Text>
          </Box>
          <Divider borderColor={panelBorder} />
          <VStack align="stretch" spacing={4}>
            <FormControl isInvalid={Boolean(errors?.name)} isRequired>
              <FormLabel mb={1} color={labelColor}>
                Nazwa
              </FormLabel>
              <Input
                size="sm"
                value={form.name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Wpisz nazwę produktu"
                borderRadius="md"
              />
              {errors?.name ? (
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              ) : null}
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl isInvalid={Boolean(errors?.sku)} isRequired>
                <FormLabel mb={1} color={labelColor}>
                  SKU
                </FormLabel>
                <Input
                  size="sm"
                  value={form.sku}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({ ...prev, sku: e.target.value }))
                  }
                  placeholder="Wprowadź numer SKU"
                  borderRadius="md"
                />
                {errors?.sku ? (
                  <FormErrorMessage>{errors.sku}</FormErrorMessage>
                ) : null}
              </FormControl>

              <FormControl>
                <FormLabel mb={1} color={labelColor}>
                  EAN
                </FormLabel>
                <Input
                  size="sm"
                  value={form.EAN}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setForm((prev) => ({ ...prev, EAN: e.target.value }))
                  }
                  placeholder="Opcjonalnie"
                  borderRadius="md"
                />
              </FormControl>

              <FormControl isInvalid={Boolean(errors?.unit)} isRequired>
                <FormLabel mb={1} color={labelColor}>
                  J.m.
                </FormLabel>
                <Select
                  size="sm"
                  value={form.unit}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, unit: e.target.value }))
                  }
                  placeholder="Wybierz jednostkę"
                  borderRadius="md"
                >
                  {form.unit &&
                  !PRODUCT_UNIT_OPTIONS.some((opt) => opt === form.unit) ? (
                    <option value={form.unit}>{form.unit}</option>
                  ) : null}
                  {PRODUCT_UNIT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
                {errors?.unit ? (
                  <FormErrorMessage>{errors.unit}</FormErrorMessage>
                ) : null}
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel mb={1} color={labelColor}>
                  Domyślny próg krytyczny
                </FormLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  precision={3}
                  step={0.001}
                  value={defaultMinQtyInput}
                  onChange={(valueString, valueNumber) =>
                    setForm((prev) => ({
                      ...prev,
                      default_min_qty:
                        valueString === "" || Number.isNaN(valueNumber)
                          ? undefined
                          : valueNumber,
                    }))
                  }
                >
                  <NumberInputField
                    placeholder="Opcjonalnie"
                    borderRadius="md"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel mb={1} color={labelColor}>
                  Cena katalogowa (%)
                </FormLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  precision={2}
                  step={0.01}
                  value={catalogPricePercentInput}
                  onChange={(valueString, valueNumber) =>
                    setForm((prev) => ({
                      ...prev,
                      catalog_price_percent:
                        valueString === "" || Number.isNaN(valueNumber)
                          ? undefined
                          : valueNumber,
                    }))
                  }
                >
                  <NumberInputField
                    placeholder="Opcjonalnie"
                    borderRadius="md"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isInvalid={Boolean(errors?.unit_weight_kg)}>
                <FormLabel mb={1} color={labelColor}>
                  Waga jednej jednostki [kg]
                </FormLabel>
                <NumberInput
                  size="sm"
                  min={0.0001}
                  precision={4}
                  step={0.0001}
                  value={unitWeightInput}
                  onChange={(valueString) =>
                    setForm((prev) => ({
                      ...prev,
                      unit_weight_kg: valueString,
                    }))
                  }
                >
                  <NumberInputField
                    placeholder="Opcjonalnie"
                    borderRadius="md"
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color={mutedText} mt={1}>
                  Masa jednej jednostki z pola J.m. (np. szt., karton lub
                  paleta), nie masa materiałów opakowaniowych PPWR.
                </Text>
                {errors?.unit_weight_kg ? (
                  <FormErrorMessage>{errors.unit_weight_kg}</FormErrorMessage>
                ) : null}
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel mb={1} color={labelColor}>
                Opis
              </FormLabel>
              <Textarea
                size="sm"
                value={form.description}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Opcjonalnie"
                borderRadius="md"
                minH="112px"
              />
            </FormControl>
          </VStack>
        </VStack>
      </Box>

      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="md"
        bg={panelBg}
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Producent i dostawcy
            </Text>
            <Text fontSize="sm" color={mutedText} mt={1}>
              Wskaż firmy powiązane z produktem, żeby operator widział właściwy
              kontekst zakupowy.
            </Text>
          </Box>
          <Divider borderColor={panelBorder} />

          <FormControl
            isInvalid={Boolean(errors?.manufacturer)}
            isRequired={manufacturerRequired}
          >
            <ContragentSelect
              label="Producent"
              contragentRole="MANUFACTURER"
              value={form.manufacturer_contragent_id}
              displayValue={
                form.manufacturer_contragent_name || form.manufacturer
              }
              onChange={(nextId, nextName) =>
                setForm((prev) => ({
                  ...prev,
                  manufacturer_contragent_id: nextId,
                  manufacturer_contragent_name: nextName,
                  manufacturer: nextName,
                }))
              }
              isRequired={manufacturerRequired}
            />
            {errors?.manufacturer ? (
              <FormErrorMessage>{errors.manufacturer}</FormErrorMessage>
            ) : null}
          </FormControl>

          <ContragentsMultiSelect
            label="Dostawcy"
            contragentRole="SUPPLIER"
            value={form.supplier_ids}
            onChange={(nextIds) =>
              setForm((prev) => ({ ...prev, supplier_ids: nextIds }))
            }
            helperText="Produkt może być powiązany z wieloma dostawcami."
          />
        </VStack>
      </Box>

      <Box
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="md"
        bg={panelBg}
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Dodatkowe ustawienia
            </Text>
            <Text fontSize="sm" color={mutedText} mt={1}>
              Rozszerz dane produktu o atrybuty własne
              {showActiveToggle ? " i status aktywności." : "."}
            </Text>
          </Box>
          <Divider borderColor={panelBorder} />

          <FormControl>
            <ExtraDataFields
              items={form.extra_data_items}
              onChange={(items) =>
                setForm((prev) => ({ ...prev, extra_data_items: items }))
              }
            />
          </FormControl>

          <FormControl>
            <HStack justify="space-between">
              <FormLabel m={0} color={labelColor}>
                Wymaga śledzenia partii (Batch Management)
              </FormLabel>
              <Switch
                isChecked={form.requires_batch_tracking}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    requires_batch_tracking: e.target.checked,
                  }))
                }
              />
            </HStack>
          </FormControl>

          <FormControl>
            <HStack justify="space-between">
              <FormLabel m={0} color={labelColor}>
                Wymaga daty ważności (Expiration Date)
              </FormLabel>
              <Switch
                isChecked={form.requires_expiration_date}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    requires_expiration_date: e.target.checked,
                  }))
                }
              />
            </HStack>
          </FormControl>

          {showActiveToggle ? (
            <FormControl>
              <HStack justify="space-between">
                <FormLabel m={0} color={labelColor}>
                  Aktywny
                </FormLabel>
                <Switch
                  isChecked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
              </HStack>
            </FormControl>
          ) : null}
        </VStack>
      </Box>
    </>
  )
}
